import React, { useLayoutEffect, useRef } from 'react';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { BookOpen, Sparkles, ArrowUpRight, Clock, Tag } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const BlogPage = () => {
  const containerRef = useRef(null);

  const posts = [
    {
      category: "Health & Wellness",
      categoryColor: "bg-primary/10 text-primary",
      date: "Mar 18, 2026",
      readTime: "5 min read",
      title: "Essential Vaccination Protocols for Puppies",
      excerpt: "Ensure your pet's protection against Rabies, Parvovirus, and more with our comprehensive guide to early-stage immunity. Every responsible pet owner should know these critical immunization milestones.",
      image: "https://images.unsplash.com/photo-1576201836106-db1758fd1c97?q=80&w=800&auto=format&fit=crop",
      featured: true
    },
    {
      category: "Surgery & Recovery",
      categoryColor: "bg-blue-500/10 text-blue-600",
      date: "Feb 22, 2026",
      readTime: "7 min read",
      title: "Post-Operative Care: What to Expect",
      excerpt: "A guardian's manual to navigating your dog's recovery phase efficiently at home after major orthopedic or soft tissue surgery.",
      image: "https://images.unsplash.com/photo-1599443015574-be5fe8a05783?q=80&w=800&auto=format&fit=crop",
      featured: false
    },
    {
      category: "Nutrition",
      categoryColor: "bg-amber-500/10 text-amber-600",
      date: "Jan 10, 2026",
      readTime: "6 min read",
      title: "Premium Diets for Active Canine Breeds",
      excerpt: "Understanding the balance of proteins and fats required for working dogs and highly active breeds performing at their physical peak.",
      image: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?q=80&w=800&auto=format&fit=crop",
      featured: false
    },
    {
      category: "Hygiene",
      categoryColor: "bg-purple-500/10 text-purple-600",
      date: "Dec 05, 2025",
      readTime: "4 min read",
      title: "Tick & Flea Prevention Strategies",
      excerpt: "Keep your pet safe from vector-borne diseases year-round with clinical-grade anti-tick management and proven prevention protocols.",
      image: "https://images.unsplash.com/photo-1517849845537-4d257902454a?q=80&w=800&auto=format&fit=crop",
      featured: false
    },
    {
      category: "Cardiology",
      categoryColor: "bg-red-500/10 text-red-600",
      date: "Nov 12, 2025",
      readTime: "8 min read",
      title: "Early Signs of Heart Disease in Dogs",
      excerpt: "Understanding the subtle first signals of cardiac conditions can be lifesaving. Here's what every dog owner needs to know.",
      image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?q=80&w=800&auto=format&fit=crop",
      featured: false
    },
    {
      category: "Orthopaedics",
      categoryColor: "bg-slate-500/10 text-slate-600",
      date: "Oct 20, 2025",
      readTime: "6 min read",
      title: "Hip Dysplasia in Large Breeds: A Complete Guide",
      excerpt: "Recognizing, diagnosing, and managing hip dysplasia in large breed dogs with modern orthopaedic approaches.",
      image: "https://images.unsplash.com/photo-1551717743-49959800b1f6?q=80&w=800&auto=format&fit=crop",
      featured: false
    }
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.blog-hero-line', { y: 60, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.12, duration: 1, ease: 'power3.out', delay: 0.2 });
      gsap.fromTo('.blog-card', { y: 40, opacity: 0, scale: 0.97 }, {
        y: 0, opacity: 1, scale: 1, stagger: 0.1, duration: 0.8, ease: 'power3.out',
        scrollTrigger: { trigger: '.blog-grid', start: 'top 80%', toggleActions: 'play none none reverse' }
      });
      gsap.to('.blog-glow', { y: '-15%', ease: 'none', scrollTrigger: { trigger: '.blog-hero', scrub: true } });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const featuredPost = posts.find(p => p.featured);
  const regularPosts = posts.filter(p => !p.featured);

  return (
    <>
      <SEO 
        title="Pet Care Blog | Veterinary Tips by Dr. Suresh"
        description="Stay updated with expert veterinary advice, pet health tips, and clinic news from Dr. Suresh Pet Care Specialist Hospital in Vanasthalipuram."
        canonical="/blog"
      />
      <motion.div ref={containerRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-white overflow-hidden">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="blog-hero pt-[140px] md:pt-[160px] pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-primary/5 z-0" />
        <div className="blog-glow absolute top-[15%] right-[5%] w-[500px] h-[500px] bg-primary/8 rounded-full blur-[120px] pointer-events-none" />

        <div className="section-container relative z-10">
          <div className="max-w-4xl">
            <div className="blog-hero-line inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full border border-primary/15 mb-10">
              <BookOpen size={13} className="text-primary" />
              <span className="text-[10px] font-black tracking-[0.25em] uppercase text-primary">Clinical Insights</span>
            </div>
            <h1 className="blog-hero-line text-navy font-display font-bold leading-[0.92] tracking-tight text-[clamp(2.5rem,7vw,5.5rem)] md:text-5xl lg:text-7xl mb-8">
              Pet Care & <br /><em className="font-normal text-primary not-italic text-3xl md:text-5xl lg:text-7xl">Veterinary Knowledge.</em>
            </h1>
            <p className="blog-hero-line text-muted-foreground text-xl md:text-2xl leading-relaxed max-w-2xl">
              Learn about pet health, surgery recovery, and preventive care from the best veterinary doctors in Vanasthalipuram, Hyderabad.
            </p>
          </div>
        </div>
      </section>

      {/* ── FEATURED POST ─────────────────────────────────── */}
      {featuredPost && (
        <section className="pb-12">
          <div className="section-container">
            <div className="blog-card relative rounded-[3.5rem] overflow-hidden border border-navy/5 shadow-2xl group cursor-pointer hover:-translate-y-1 transition-all duration-700">
              <div className="grid lg:grid-cols-2 h-full">
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <img src={featuredPost.image} alt={featuredPost.title}
                    className="w-full h-full object-cover transition-transform duration-[3000ms] group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-r from-navy/30 to-transparent" />
                  <span className="absolute top-6 left-6 px-4 py-2 bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-full">Featured Article</span>
                </div>
                <div className="p-10 md:p-16 flex flex-col justify-center bg-white space-y-6">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide ${featuredPost.categoryColor}`}>{featuredPost.category}</span>
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <Clock size={12} /> {featuredPost.readTime}
                    </div>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-navy group-hover:text-primary transition-colors duration-500 leading-tight">{featuredPost.title}</h2>
                  <p className="text-muted-foreground leading-relaxed">{featuredPost.excerpt}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <span className="text-xs text-muted-foreground font-medium">{featuredPost.date}</span>
                    <div className="inline-flex items-center gap-2 text-primary font-bold text-sm group-hover:gap-4 transition-all duration-500">
                      Read Article <ArrowUpRight size={15} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── ARTICLES GRID ─────────────────────────────────── */}
      <section className="py-12 pb-24">
        <div className="section-container">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-primary font-black uppercase tracking-[0.2em] text-[11px] mb-2">All Articles</p>
              <h2 className="text-navy font-bold text-4xl">Clinical <em className="font-normal text-primary not-italic">Library.</em></h2>
            </div>
            <div className="hidden md:flex items-center gap-3">
              {['All', 'Surgery', 'Health', 'Nutrition'].map((cat) => (
                <button key={cat} className={`px-5 py-2 rounded-full text-xs font-bold transition-all border ${cat === 'All' ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-border hover:border-navy'}`}>{cat}</button>
              ))}
            </div>
          </div>

          <div className="blog-grid grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularPosts.map((post, i) => (
              <div key={i} className="blog-card group relative bg-white border border-navy/5 rounded-[2.5rem] overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-700 cursor-pointer">
                {/* Image */}
                <div className="relative h-52 overflow-hidden">
                  <img src={post.image} alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <span className={`absolute top-4 left-4 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide ${post.categoryColor} backdrop-blur-sm`}>{post.category}</span>
                </div>

                {/* Content */}
                <div className="p-8 space-y-4">
                  <div className="flex items-center gap-3 text-muted-foreground text-xs">
                    <div className="flex items-center gap-1"><Clock size={11} /> {post.readTime}</div>
                    <div className="flex items-center gap-1"><Tag size={11} /> {post.date}</div>
                  </div>
                  <h3 className="font-bold text-navy text-lg leading-tight group-hover:text-primary transition-colors duration-500">{post.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center gap-2 text-primary font-bold text-xs pt-2 group-hover:gap-4 transition-all duration-500">
                    Read Article <ArrowUpRight size={13} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEWSLETTER CTA ─────────────────────────────────── */}
      <section className="py-16 pb-24">
        <div className="section-container">
          <div className="relative rounded-[4rem] overflow-hidden bg-[#010816] p-12 md:p-20 text-center">
            <div className="absolute top-0 left-[20%] w-[600px] h-[300px] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-8">
                <Sparkles size={13} className="text-primary" />
                <span className="text-[10px] font-black tracking-[0.25em] uppercase text-white/60">Clinical Updates</span>
              </div>
              <h2 className="text-white font-bold text-4xl md:text-5xl mb-6">
                Stay Informed, <br /><em className="font-normal text-primary not-italic">Stay Ahead.</em>
              </h2>
              <p className="text-white/60 text-lg mb-10">
                Expert veterinary insights, seasonal health advisories, and clinical updates delivered to your inbox from our medical team.
              </p>
              <a href="https://wa.me/919848954921" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-black text-sm tracking-widest hover:shadow-[0_20px_40px_rgba(3,137,3,0.4)] transition-all duration-500">
                <BookOpen size={16} /> Subscribe via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
      </motion.div>
    </>
  );
};

export default BlogPage;
